import React, { useState, useEffect } from 'react';
import { useParams } from "react-router-dom";
import { getDataWithComp, getData, getAlert, updateData } from "../../../utils/SamuiUtils";

const ServiceDetail = () => {
  const { id: jobId, time: jobTime } = useParams();

  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [existingImages, setExistingImages] = useState([]); // รูปเก่าจาก API
  const [newImages, setNewImages] = useState([]); // รูปใหม่ที่เพิ่งอัปโหลด
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');

  // Status options for dropdown
  const statusOptions = [
    { value: 1, label: 'รอเข้าพบ', color: 'warning' },
    { value: 2, label: 'Checkin', color: 'primary' },
    { value: 3, label: 'เลื่อนนัด', color: 'secondary' },
    { value: 4, label: 'ปิด Job', color: 'success' },
    { value: 13, label: 'ยกเลิก', color: 'danger' }
  ];

  useEffect(() => {
    loadServiceData();
  }, [jobId, jobTime]);

  useEffect(() => {
    if (service) {
      setSelectedStatus(service.status || '');
    }
  }, [service]);

  const loadServiceData = async () => {
    try {
      setLoading(true);
      const data = await getData("API_1801_JOB", `job_id = ${jobId} AND job_time = ${jobTime}`);
      const imageData = await getData("Upload_File_List", `DocCode_ID = 9 AND doc_id = ${jobId} AND file_status = 'Y' AND job_time = ${jobTime}`);
      // console.log("Image Data:", imageData);

      if (!data || data.length === 0) {
        getAlert("ไม่พบข้อมูลบริการ", "warning");
        return;
      }

      const found = data[0];
      setService(found);

      // ถ้ามี imageData ให้แปลงเป็น existing images
      if (imageData && imageData.length > 0) {
        const fetchedImages = imageData.map(img => ({
          id: img.file_id || Date.now() + Math.random(),
          src: img.File_Name,
          isExisting: true // มาร์คว่าเป็นรูปเก่า
        }));
        setExistingImages(fetchedImages);
      } else {
        setExistingImages([]);
      }

    } catch (error) {
      getAlert("FAILED", "ไม่สามารถโหลดข้อมูลบริการได้: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (event) => {
    const newStatus = parseInt(event.target.value);
    if (!newStatus || newStatus === service.status) return;

    try {
      setLoading(true);
      await updateData("JOB_D", { job_status: newStatus }, { job_id: jobId, job_time: jobTime });

      getAlert("OK", "อัพเดทสถานะเรียบร้อยแล้ว");
      setSelectedStatus(newStatus);
      loadServiceData();
    } catch (error) {
      getAlert("FAILED", "ไม่สามารถอัพเดทสถานะได้: " + error.message);
      setSelectedStatus(service.status);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const newImage = {
          id: Date.now() + Math.random(),
          src: e.target.result,
          name: file.name,
          size: file.size,
          isExisting: false // มาร์คว่าเป็นรูปใหม่
        };
        setNewImages(prev => [...prev, newImage]);
      };
      reader.readAsDataURL(file);
    });
  };

  const uploadFilesToAPI = async () => {
    const compId = localStorage.getItem("company") || 3; 
    const empName = localStorage.getItem("name") || service?.Emp_Name || "ไม่ระบุ";

    if (newImages.length === 0) {
      getAlert("FAILED", "ไม่มีรูปภาพใหม่ที่ต้องการอัปโหลด");
      return;
    }

    setUploading(true);

    const successfulUploads = [];

    try {
      for (const img of newImages) {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/upload-file`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            comp_id: compId,
            doc_id: jobId,
            DocCode_ID: 9,
            job_time: jobTime,
            upload_by: empName,
            file_status: "Y",
            files: [img.src]
          })
        });

        const result = await response.json();
        if (result.status === "SUCCESS") {
          successfulUploads.push(result.files?.[0]);
        } else {
          console.warn("⚠️ Upload failed for image:", img.name);
        }
      }

      if (successfulUploads.length > 0) {
        getAlert("OK", `อัปโหลดสำเร็จ ${successfulUploads.length} รูป`);
        setNewImages([]); // ล้างรูปใหม่หลังอัปโหลดเสร็จ
        loadServiceData(); // โหลดข้อมูลใหม่เพื่อดึงรูปที่อัปโหลดแล้ว
      } else {
        getAlert("FAILED", "ไม่สามารถอัปโหลดรูปได้");
      }

    } catch (error) {
      console.error("Error uploading:", error);
      getAlert("FAILED", "เกิดข้อผิดพลาดระหว่างอัปโหลดไฟล์");
    } finally {
      setUploading(false);
    }
  };

  const removeNewImage = (imageId) => {
    setNewImages(prev => prev.filter(img => img.id !== imageId));
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 1:
      case 'รอเข้าพบ':
        return 'bg-warning bg-opacity-75 text-dark';
      case 2:
      case 'Checkin':
        return 'bg-primary bg-opacity-75 text-white';
      case 3:
      case 'เลื่อนนัด':
        return 'bg-secondary bg-opacity-75 text-white';
      case 4:
      case 'ปิด Job':
        return 'bg-success bg-opacity-75 text-white';
      case 13:
      case 'ยกเลิก':
        return 'bg-danger bg-opacity-75 text-white';
      default:
        return 'bg-light text-dark';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1: return 'รอเข้าพบ';
      case 2: return 'Checkin';
      case 3: return 'เลื่อนนัด';
      case 4: return 'ปิด Job';
      case 13: return 'ยกเลิก';
      default: return status;
    }
  };

  // Component สำหรับแสดงรูปภาพ
  const ImageGallery = ({ images, title, showRemoveButton = false, onRemove = null, emptyMessage }) => (
    <div className="mb-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h6 className="mb-0 text-muted fw-bold">
          <i className="fas fa-images me-2"></i>
          {title} ({images.length})
        </h6>
      </div>
      
      {images.length > 0 ? (
        <div className="row g-3">
          {images.map((image) => (
            <div key={image.id} className="col-6 col-sm-4">
              <div className="position-relative">
                <img
                  src={image.src}
                  alt={image.name || 'รูปภาพ'}
                  className="img-fluid rounded-3 shadow-sm w-100"
                  style={{
                    aspectRatio: '1:1',
                    objectFit: 'cover',
                    cursor: 'pointer',
                    border: '2px solid #e9ecef'
                  }}
                  onClick={() => setSelectedImage(image)}
                />
                {showRemoveButton && onRemove && (
                  <button
                    className="btn btn-danger btn-sm position-absolute"
                    style={{
                      top: '8px',
                      right: '8px',
                      width: '32px',
                      height: '32px',
                      fontSize: '0.8rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onClick={() => onRemove(image.id)}
                  >
                    <i className="fa fa-trash"></i>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center text-muted py-4 bg-light rounded-3">
          <i className="fas fa-images fa-2x mb-2 text-secondary"></i>
          <p className="mb-0">{emptyMessage}</p>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="row justify-content-center">
          <div className="col-12">
            <div className="text-center">
              <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">กำลังโหลดข้อมูล...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="container-fluid py-4">
        <div className="row justify-content-center">
          <div className="col-12">
            <div className="alert alert-warning text-center border-0 shadow-sm" role="alert">
              <div className="mt-2">ไม่พบข้อมูลบริการ</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3 px-3" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <div className="row">
        <div className="col-12">
          {/* Header Section - Mobile Optimized */}
          <div className="text-center mb-4">
            {/* Status Badge */}
            <span className={`badge ${getStatusBadge(service.status)} fs-6 px-4 py-2 rounded-pill shadow-sm`}>
              {getStatusText(service.status)}
            </span>
          </div>

          {/* Main Card - Mobile Optimized */}
          <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '1rem' }}>
            <div className="card-header text-white border-0" style={{
              background: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
              borderRadius: '1rem 1rem 0 0'
            }}>
              <h6 className="card-title mb-0 d-flex align-items-center text-white">
                ข้อมูลงานบริการ
              </h6>
            </div>

            <div className="card-body p-4">
              {/* Service Info - Mobile Stack Layout */}
              <div className="row g-3">
                <div className="col-12">
                  <div className="bg-light border-0 rounded-3 p-3 shadow-sm">
                    <label className="form-label text-muted mb-2 small d-flex align-items-center">
                      เลขที่งานบริการ
                    </label>
                    <div className="fw-bold text-primary fs-5">{service.job_no}</div>
                    <small className="text-muted">ครั้งที่: {service.job_time}</small>
                  </div>
                </div>

                <div className="col-12">
                  <div className="bg-light border-0 rounded-3 p-3 shadow-sm">
                    <label className="form-label text-muted mb-2 small d-flex align-items-center">
                      สถานะงานบริการ
                    </label>
                    <div className="mt-4">
                      <select
                        className="form-select form-select-lg border-2 rounded-3 shadow-sm"
                        value={selectedStatus}
                        onChange={handleStatusChange}
                        disabled={loading}
                        style={{
                          fontSize: '1.1rem',
                          padding: '12px 16px',
                          minHeight: '50px',
                          background: 'white',
                          border: '2px solid #dee2e6'
                        }}
                      >
                        <option value="">
                          {service.job_status_name || 'เลือกสถานะ'}
                        </option>

                        {statusOptions
                          .filter(option => option.value !== service.job_status)
                          .map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                      </select>

                      <small className="text-muted mt-1 d-block">
                        <i className="fas fa-info-circle me-1"></i>
                        เลือกสถานะใหม่เพื่อเปลี่ยนแปลงทันที
                      </small>
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  <div className="bg-light border-0 rounded-3 p-3 shadow-sm">
                    <label className="form-label text-muted mb-2 small d-flex align-items-center">
                      วันที่นัดหมาย
                    </label>
                    <div className="fw-bold text-dark">{service.job_date ? new Date(service.job_date).toLocaleDateString('th-TH', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : 'ไม่มีข้อมูล'}</div>
                  </div>
                </div>

                <div className="col-12">
                  <div className="bg-light border-0 rounded-3 p-3 shadow-sm">
                    <label className="form-label text-muted mb-2 small d-flex align-items-center">
                      รายละเอียดลูกค้า
                    </label>
                    <div className="fw-bold text-dark mb-2">{service.AR_Name}</div>
                    <small className="text-muted d-block mb-1">รหัส: {service.AR_Code}</small>
                    <small className="text-muted d-block mb-1">
                      ที่อยู่: {
                        service.AR_Add1 || service.AR_Add2 || service.AR_Add3 || service.AR_Province || service.AR_Zipcode
                          ? [
                            service.AR_Add1,
                            service.AR_Add2,
                            service.AR_Add3,
                            service.AR_Province,
                            service.AR_Zipcode
                          ]
                            .filter(Boolean)
                            .join(', ')
                          : 'ไม่มีข้อมูลที่อยู่'
                      }
                    </small>
                    <small className="text-muted d-block">
                      เบอร์โทร: {
                        [service.AR_Tel1, service.AR_Tel2].filter(Boolean).join(" / ") || "ไม่มีข้อมูล"
                      }
                    </small>
                  </div>
                </div>

                <div className="col-12">
                  <div className="bg-light border-0 rounded-3 p-3 shadow-sm">
                    <label className="form-label text-muted mb-2 small d-flex align-items-center">
                      พนักงานผู้รับผิดชอบ
                    </label>
                    <div className="fw-bold text-dark">{service.Emp_Name}</div>
                  </div>
                </div>

                <div className="col-12">
                  <div className="bg-light border-0 rounded-3 p-3 shadow-sm">
                    <label className="form-label text-muted mb-2 small d-flex align-items-center">
                      รายละเอียดการเข้าบริการ
                    </label>
                    <div className="fw-bold text-dark">{service.job_desc}</div>
                  </div>
                </div>

                <div className="col-12">
                  <div className="bg-light border-0 rounded-3 p-3 shadow-sm">
                    <label className="form-label text-muted mb-2 small d-flex align-items-center">
                      รายละเอียดการปฏิบัติงาน
                    </label>
                    <div className="fw-bold text-dark">{service.job_detail}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Image Gallery Section */}
          <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '1rem' }}>
            <div className="card-header text-white border-0" style={{
              background: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
              borderRadius: '1rem 1rem 0 0'
            }}>
              <h6 className="card-title mb-0 d-flex align-items-center justify-content-between text-white">
                <span>
                  <i className="fas fa-folder-open me-2"></i>
                  รูปภาพงานทั้งหมด
                </span>
                <label className="btn btn-light btn-sm mb-0 rounded-pill px-3">
                  <i className="fas fa-plus me-1"></i>
                  เพิ่มรูป
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                </label>
              </h6>
            </div>

            <div className="card-body p-4">
              {/* รูปภาพที่มีอยู่แล้ว (จาก API) */}
              <ImageGallery
                images={existingImages}
                title="รูปภาพที่มีอยู่แล้ว"
                showRemoveButton={false}
                emptyMessage="ยังไม่มีรูปภาพที่บันทึกไว้"
              />

              {/* รูปภาพใหม่ที่เพิ่งเลือก */}
              {newImages.length > 0 && (
                <>
                  <hr className="my-4" />
                  <ImageGallery
                    images={newImages}
                    title="รูปภาพใหม่ที่เลือก (ยังไม่ได้อัปโหลด)"
                    showRemoveButton={true}
                    onRemove={removeNewImage}
                    emptyMessage="ไม่มีรูปภาพใหม่ที่เลือก"
                  />
                </>
              )}

              {/* Upload Status */}
              {uploading && (
                <div className="text-center mb-3 p-3 bg-primary bg-opacity-10 rounded-3 border border-primary border-opacity-25">
                  <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                  <span className="text-primary fw-bold">กำลังอัปโหลดรูปภาพ...</span>
                </div>
              )}

              {/* Upload Button */}
              {newImages.length > 0 && (
                <div className="d-grid mt-4">
                  <button
                    className="btn btn-primary btn-lg rounded-3 shadow-sm"
                    onClick={uploadFilesToAPI}
                    disabled={uploading}
                    style={{
                      fontSize: '1.1rem',
                      padding: '12px 24px',
                      background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
                      border: 'none'
                    }}
                  >
                    {uploading ? (
                      <>
                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                        กำลังอัปโหลด...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-cloud-upload-alt me-2"></i>
                        อัปโหลดรูปภาพใหม่ ({newImages.length} รูป)
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Image Modal */}
          {selectedImage && (
            <div
              className="modal fade show d-block"
              style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1055 }}
              onClick={() => setSelectedImage(null)}
            >
              <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content border-0" style={{ borderRadius: '1rem' }}>
                  <div className="modal-header border-0">
                    <h6 className="modal-title">
                      {selectedImage.name || 'รูปภาพ'}
                      {selectedImage.isExisting ? 
                        <span className="badge bg-success ms-2">บันทึกแล้ว</span> : 
                        <span className="badge bg-warning ms-2">ยังไม่บันทึก</span>
                      }
                    </h6>
                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      onClick={() => setSelectedImage(null)}
                    ></button>
                  </div>
                  <div className="modal-body p-0">
                    <img
                      src={selectedImage.src}
                      alt={selectedImage.name || 'รูปภาพ'}
                      className="img-fluid w-100"
                      style={{ borderRadius: '0 0 1rem 1rem' }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceDetail;